//service/[slug].js

import Head from "next/head";
import { useRouter } from "next/router";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ServicesDetails from "../../components/services-details";
// import Mission1 from "../../components/mission1";
import Process from "../../components/process";
import AsSeenSection2 from "../../components/common/AsSeenSection2";
import TestimonialsSection from "../../components/common/TestimonialsSection";
import Clients from "../../components/clients";
import SubServicesList from "../../components/sub-services-list";
import BlogSection from "../../components/blog-section";
import FaqSection from "../../components/common/FaqSection";
import Footer from "../../components/footer";
import Header from "../../components/common/Header";
import ServicesBanner from "../../components/common/ServicesBanner";
import {
  fetchParentServicesDetails,
  // fetchParentServicesData,
} from "../../components/http/commonApi";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { addDofollowToAnchors } from "../../utils/modifyHtmlLinks";
import { captureUTMParams } from "../../utils/utm";
import { useEffect } from "react";
import IndustryTestimonials from "../../components/common/IndustryTestimonials";
import CaseStudyVideos from "../../components/case-study-videos";
import { data } from "autoprefixer";
import HreflangLinks from "../../components/common/HrefLinkTags";
import { safeParseSchema } from "../../utils/schemaUtils";

export default function ServicesInner({ serviceDetails, locale, slug }) {
  const router = useRouter();

  // useEffect(() => {
  //   captureUTMParams();
  // }, []);

  if (router.isFallback) {
    return <LoadingSpinner message="Loading service details..." />;
  }

  const processSectionRaw = serviceDetails?.sections?.find((section) => {
    const title = (section.title || "").toLowerCase();
    return title.includes("our") && title.includes("process");
  });

  // Belt-and-braces: even though getServerSideProps already normalizes
  // these fields, safeParseSchema() is cheap and guarantees we never hand
  // a string straight to JSON.stringify() here (which would double-encode
  // it and produce broken JSON-LD in the page source).
  const activeFaqSchema = safeParseSchema(
    locale === "ar" ? serviceDetails?.faqSchema_ar : serviceDetails?.faqSchema,
  );
  const activeServiceSchema = safeParseSchema(
    locale === "ar"
      ? serviceDetails?.serviceSchema_ar
      : serviceDetails?.serviceSchema,
  );

  return (
    <>
      {/* <HreflangLinks
        enPath={`/${serviceDetails?.slug_en}`}
        arPath={`/${serviceDetails?.slug_en}`} // same slug, just under /ar
      /> */}

      <HreflangLinks
        enPath={`/${serviceDetails?.slug_en}`}
        arPath={
          serviceDetails?.hasArabic ? `/ar/${serviceDetails.slug_en}` : null
        }
      />

      <Head>
        <title>
          {serviceDetails?.metaTitle ||
            "Prism Digital | Top Digital Marketing Agency in UAE"}
        </title>
        <meta
          name="description"
          content={
            serviceDetails?.metaDescription ||
            "A leading digital marketing agency in UAE - Prism Digital, offering SEO, PPC, social media, and web design services to grow your brand online. Book us today!"
          }
        />
        {locale === "ar" ? (
          <link
            rel="canonical"
            href={`https://www.prism-me.com/${locale}/${serviceDetails?.slug_en}`}
          />
        ) : (
          <link
            rel="canonical"
            href={`https://www.prism-me.com/${serviceDetails?.slug_en}`}
          />
        )}
        {/* Open Graph Tags */}
        <meta
          property="og:title"
          content={
            locale === "ar"
              ? serviceDetails?.ogTitle_ar
              : serviceDetails?.ogTitle
          }
        />
        <meta
          property="og:description"
          content={
            locale === "ar"
              ? serviceDetails?.ogDescription_ar
              : serviceDetails?.ogDescription
          }
        />
        {serviceDetails?.ogimageurl && (
          <meta
            property="og:image"
            content={
              locale === "ar"
                ? serviceDetails?.ogimageurl_ar
                : serviceDetails?.ogimageurl
            }
          />
        )}
        {serviceDetails?.ogImageAlt && (
          <meta
            property="og:image:alt"
            content={
              locale === "ar"
                ? serviceDetails?.ogImageAlt_ar
                : serviceDetails?.ogImageAlt
            }
          />
        )}
        {serviceDetails?.ogUrl && (
          <meta
            property="og:url"
            content={
              locale === "ar" ? serviceDetails?.ogUrl_ar : serviceDetails?.ogUrl
            }
          />
        )}
        <meta
          property="og:type"
          content={serviceDetails?.ogType || "website"}
        />
        <meta
          property="og:site_name"
          content={serviceDetails?.ogSiteName || "Prism Digital"}
        />
        <meta
          property="og:locale"
          content={locale === "ar" ? "ar_AE" : "en_US"}
        />

        {/* JSON-LD Structured Data */}
        {Object.keys(activeFaqSchema).length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(activeFaqSchema),
            }}
          />
        )}

        {Object.keys(activeServiceSchema).length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(activeServiceSchema),
            }}
          />
        )}
      </Head>

      <Header />
      <main className="w-full box-border bg-background-color-primary overflow-hidden">
        {/* <CommonBanner2 name={serviceDetails?.bannertitle} /> */}
        <ServicesBanner
          name={serviceDetails?.bannertitle}
          breadcrumbProps={{
            labelMap: {
              home: locale === "ar" ? "الرئيسية" : "Home",
              services: locale === "ar" ? "الخدمات" : "Services",
            },
            dynamicLabel: serviceDetails?.menuname,
            separator: locale === "ar" ? "‹" : ">",
          }}
          slug={slug}
        />
        {(serviceDetails?.updatedAt || serviceDetails?.createdAt) && (
          <section className="w-full box-border px-6 sm:px-6 lg:px-20 pt-[20px] lg:pt-[40px] text-text-primary max-w-[1600px] mx-auto text-end">
            {locale === "ar" ? "آخر تحديث:" : "Last Updated:"}{" "}
            {new Date(
              serviceDetails?.updatedAt || serviceDetails?.createdAt,
            ).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </section>
        )}

        <ServicesDetails
          serviceIntro={{
            title: serviceDetails?.title,
            description: serviceDetails?.introduction,
          }}
        />
        {serviceDetails?.bulletPoints?.[0]?.points?.length > 0 && (
          <section className="w-full self-stretch flex flex-row items-start justify-start pt-0 px-0 mb-[40px] lg:mb-[60px] box-border max-w-full bg-text-primary text-background-color-primary ">
            <div className="flex-1  overflow-hidden flex flex-col items-start justify-start py-[40px] lg:py-[60px] px-6 sm:px-6 lg:px-20 box-border gap-8 max-w-[1600px] mx-auto">
              <h2 className="m-0 w-full relative text-28px sm:text-[32px] lg:text-[40px] leading-[120%] font-medium font-[inherit] inline-block max-w-full">
                {serviceDetails?.bulletPoints?.[0]?.title}
              </h2>
              <div className="w-full relative text-[16px] leading-[150%] inline-block max-w-full">
                <ul className="m-0 flex flex-col items-start justify-start gap-3">
                  {serviceDetails?.bulletPoints?.[0]?.points?.map((item, i) => (
                    <li
                      key={i}
                      className="w-full relative text-[16px] leading-[150%] text-start wrapListIem"
                      dangerouslySetInnerHTML={{
                        __html: addDofollowToAnchors(item) || "",
                      }}
                    />
                    //   {item}
                    // </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        <section
          className={`w-full flex flex-col gap-6 items-start justify-center px-6 sm:px-6 lg:px-20 pb-[40px] lg:pb-[60px] box-border max-w-[1600px] mx-auto`}
        >
          <h2 className="m-0 text-[28px] sm:text-[32px] lg:text-[40px] leading-[120%] font-medium">
            {serviceDetails?.secondaryHeading}
          </h2>
          <div
            className="text-[16px] leading-[150%] m-0 rich-text"
            dangerouslySetInnerHTML={{
              __html:
                addDofollowToAnchors(serviceDetails?.secondaryDescription) ||
                "",
            }}
          />
          {/* {serviceDetails?.secondaryDescription}
          </p> */}
        </section>

        {serviceDetails?.process?.[0]?.content?.length > 0 && (
          <Process
            title={serviceDetails?.process?.[0]?.title}
            processData={serviceDetails?.process?.[0]?.content}
          />
        )}

        <AsSeenSection2 />

        {serviceDetails?.testimonialVideos?.length > 0 && (
          <IndustryTestimonials
            testimonials={serviceDetails?.testimonialVideos}
          />
        )}
        <Clients />
        {serviceDetails?.casestudyvideos?.length > 0 && (
          <CaseStudyVideos casestudyvideos={serviceDetails?.casestudyvideos} />
        )}

        {
          // !processSectionRaw &&

          serviceDetails?.sections?.[0]?.content?.length > 0 && (
            <SubServicesList
              title={serviceDetails?.sections?.[0]?.title}
              subServices={serviceDetails?.sections?.[0]?.content}
              childServices={serviceDetails?.childServices}
              parentSlug={serviceDetails?.slug_en}
            />
          )
        }

        <BlogSection />
        <FaqSection faqs={serviceDetails?.faqs} />
      </main>
      <Footer />
    </>
  );
}

export async function getServerSideProps({ params, locale, res }) {
  try {
    const { data: serviceDetails } = await fetchParentServicesDetails(
      params.slug,
      locale,
    );

    if (!serviceDetails) {
      return {
        notFound: true,
      };
    }

    // ✅ Set cache headers
    res.setHeader(
      "Cache-Control",
      "public, s-maxage=60, stale-while-revalidate=300",
    );

    // ✅ Normalize schema fields (serviceSchema / faqSchema and their _ar
    // counterparts) into real objects before they're sent to the client.
    // The API sometimes returns these as JSON strings, or even
    // double-encoded JSON strings — passing those straight through and
    // then JSON.stringify()'ing them again in the component is what
    // produces broken/escaped JSON-LD (\\\" everywhere) in the page's
    // <script type="application/ld+json"> output.
    const normalizedServiceDetails = {
      ...serviceDetails,
      serviceSchema: safeParseSchema(serviceDetails.serviceSchema),
      faqSchema: safeParseSchema(serviceDetails.faqSchema),
      serviceSchema_ar: safeParseSchema(serviceDetails.serviceSchema_ar),
      faqSchema_ar: safeParseSchema(serviceDetails.faqSchema_ar),
    };

    return {
      props: {
        serviceDetails: normalizedServiceDetails,
        locale,
        slug: params.slug,
        ...(await serverSideTranslations(locale, [
          "header",
          "footer",
          "services",
          "common",
        ])),
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}
